import { useLocation, useNavigate } from 'react-router-dom';
import { ClipboardList, ListChecks } from 'lucide-react';
import { Pastille, type TonPastille } from '../ui/Primitives';
import { adresseDevoir } from '../../lib/affichage-devoirs';
import type { DevoirDuProgramme } from '../../lib/useLecteur';

/**
 * Les devoirs et quiz d'un module, ou de la fin du cours (TF-454), dans le
 * programme du lecteur et dans « Cours en ligne » : l'apprenant les ouvre là
 * où il suit le cours, et le quiz le ramène ensuite à cet écran (`?retour=`).
 * L'aperçu de l'auteur les liste, brouillons signalés, sans les ouvrir : un
 * quiz se passe depuis l'espace apprenant.
 */

function noteLisible(note: number): string {
  return String(note).replace('.', ',');
}

/** Où en est l'apprenant, tel que l'API le dit ; l'écran ne juge rien. */
function etat(devoir: DevoirDuProgramme): { texte: string; ton: TonPastille } {
  if (devoir.brouillon) return { texte: 'Brouillon', ton: 'neutral' };
  if (devoir.statut === null) return { texte: 'Publié', ton: 'success' };
  if (devoir.statut === 'CORRIGE') {
    return { texte: devoir.note !== null ? `Corrigé · ${noteLisible(devoir.note)}/20` : 'Corrigé', ton: 'success' };
  }
  if (devoir.statut === 'RENDU') {
    if (devoir.note !== null) return { texte: `Fait · ${noteLisible(devoir.note)}/20`, ton: 'success' };
    return { texte: devoir.type === 'QUIZ' ? 'Fait' : 'Rendu', ton: 'success' };
  }
  if (devoir.enCours) return { texte: 'En cours', ton: 'info' };
  if (devoir.enRetard) return { texte: 'En retard', ton: 'danger' };
  return { texte: devoir.type === 'QUIZ' ? 'À faire' : 'À rendre', ton: 'warning' };
}

/** Adresse d'un devoir ouvert depuis le lecteur : le quiz y revient une fois fini. */
function adresseDevoirDepuis(devoir: Pick<DevoirDuProgramme, 'id' | 'type'>, retour: string): string {
  const adresse = adresseDevoir(devoir);
  return devoir.type === 'QUIZ' ? `${adresse}?retour=${encodeURIComponent(retour)}` : adresse;
}

export function DevoirsDuModule({
  devoirs,
  taille = 'compacte',
}: {
  devoirs: readonly DevoirDuProgramme[];
  taille?: 'compacte' | 'large';
}): JSX.Element | null {
  const navigate = useNavigate();
  const lieu = useLocation();
  if (devoirs.length === 0) return null;
  const retour = `${lieu.pathname}${lieu.search}`;
  return (
    <ul className="mt-1 space-y-1" aria-label="Évaluations">
      {devoirs.map((devoir) => {
        const Icone = devoir.type === 'QUIZ' ? ListChecks : ClipboardList;
        const pastille = etat(devoir);
        const contenu = (
          <>
            <Icone className={`${taille === 'large' ? 'h-5 w-5' : 'h-4 w-4'} shrink-0 text-codialis-sky`} aria-hidden />
            <span className="sr-only">{devoir.type === 'QUIZ' ? 'Quiz :' : 'Devoir :'}</span>
            <span className={`min-w-0 flex-1 truncate ${taille === 'large' ? 'text-sm font-medium' : 'text-sm'}`} title={devoir.titre}>
              {devoir.titre}
            </span>
            <Pastille texte={pastille.texte} ton={pastille.ton} />
          </>
        );
        const classe = `flex min-h-[44px] w-full items-center gap-2 rounded-lg px-2 text-left text-codialis-navy ${
          taille === 'large' ? 'gap-3 py-2' : ''
        }`;
        return (
          <li key={devoir.id}>
            {devoir.statut === null ? (
              <div className={classe} title="L’apprenant l’ouvre depuis son espace">
                {contenu}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => navigate(adresseDevoirDepuis(devoir, retour))}
                className={`${classe} transition hover:bg-gray-50`}
              >
                {contenu}
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}
